import React from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { UsePermissionVerification } from "components/customHooks"
import { PERMISSIONS, MARKETPLACE_PERMISSIONS } from "constants/permission"
import { LeftPanelWrapper, PanelMenu, PanelMenuItem } from "./LeftPanel.style"

// TODO: set true once MARKETPLACE_CATEGORY_LIST / PERMISSIONS.MARKETPLACE is reliable in all builds.
const MARKETPLACE_MENU_ENABLED = true

const LeftPanel = () => {
  let navigate = useNavigate()
  const location = useLocation()

  const AccessControl = UsePermissionVerification()
  const canAccessPeriodicReports =
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_LIST]) ||
    AccessControl.VerifyAccess([PERMISSIONS.ORDER.ORDER_LIST])
  const canAccessBetaFeatures =
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_LIST]) ||
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_QUERY_BY_KEY]) ||
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_EDIT]) ||
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_ADD])
  const canAccessOnboarding =
    AccessControl.VerifyAccess([PERMISSIONS.MODAL.MODAL_LIST]) ||
    AccessControl.VerifyAccess([PERMISSIONS.MODAL.MODAL_EDIT]) ||
    AccessControl.VerifyAccess([PERMISSIONS.MODAL.MODAL_ADD])
  const canAccessAiPlatformAdmin =
    AccessControl.VerifyAccess([PERMISSIONS.AI_PLATFORM.AI_REVIEW_LIST]) ||
    AccessControl.VerifyAccess([PERMISSIONS.AI_AGENT.AI_AGENT_LIST]) ||
    AccessControl.VerifyAccess([PERMISSIONS.MCP.MCP_USER_LIST]) ||
    AccessControl.VerifyAccess([PERMISSIONS.MCP.MCP_AUDIT_LIST])

  const menuItems = []
  const canAccessInsights = AccessControl.VerifyAccess([
    PERMISSIONS.STAT.ADMIN_STAT_ANALYTIC_DATA,
  ])

  // Menu for Content Management V1
  ;(AccessControl.VerifyAccessByCategory(PERMISSIONS.CATEGORY) ||
    AccessControl.VerifyAccessByCategory(PERMISSIONS.MODAL) ||
    AccessControl.VerifyAccessByCategory(PERMISSIONS.FAQ) ||
    AccessControl.VerifyAccessByCategory(PERMISSIONS.START_HERE) ||
    AccessControl.VerifyAccessByCategory(PERMISSIONS.REVIEW) ||
    AccessControl.VerifyAccessByCategory(PERMISSIONS.AI_AGENT) ||
    AccessControl.VerifyAccessByCategory(PERMISSIONS.EVENT) ||
    AccessControl.VerifyAccessByCategory(PERMISSIONS.EXPERT)) &&
    menuItems.push({
      groupText: "Content Management",
      items: [],
    })

  AccessControl.VerifyAccess([PERMISSIONS.CATEGORY.CATEGORY_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "Content Management")
      .items.push({ text: "Categories", route: "/category" })

  AccessControl.VerifyAccess([PERMISSIONS.PROGRAM.PROGRAM_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "Content Management")
      .items.push({ text: "Programs", route: "/program" })

  AccessControl.VerifyAccess([PERMISSIONS.PROGRAM.PROGRAM_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "Content Management")
      .items.push({ text: "Rails", route: "/rail" })

  AccessControl.VerifyAccess([
    PERMISSIONS.START_HERE.START_HERE_RESOURCE_LIST,
  ]) &&
    menuItems
      .find((menu) => menu.groupText === "Content Management")
      .items.push({ text: "Start Resource", route: "/startHere" })

  AccessControl.VerifyAccess([PERMISSIONS.MODAL.MODAL_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "Content Management")
      .items.push({ text: "Modals", route: "/modal" })

  AccessControl.VerifyAccess([PERMISSIONS.FAQ.FAQ_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "Content Management")
      .items.push({ text: "FAQs", route: "/faq" })

  AccessControl.VerifyAccess([PERMISSIONS.REVIEW.REVIEW_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "Content Management")
      .items.push({ text: "Reviews", route: "/review" })

  AccessControl.VerifyAccess([PERMISSIONS.EXPERT.EXPERT_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "Content Management")
      .items.push({ text: "Experts", route: "/expert" })

  // Community section (reports + events)
  if (
    AccessControl.VerifyAccess([PERMISSIONS.EVENT.EVENT_LIST]) ||
    AccessControl.VerifyAccess([PERMISSIONS.COMMUNITY.REPORTED_POSTS_LIST]) ||
    AccessControl.VerifyAccess([PERMISSIONS.COMMUNITY.REPORTED_COMMENTS_LIST])
  ) {
    menuItems.push({
      groupText: "Community",
      items: [],
    })

    AccessControl.VerifyAccess([PERMISSIONS.EVENT.EVENT_LIST]) &&
      menuItems
        .find((menu) => menu.groupText === "Community")
        .items.push({ text: "Events", route: "/event" })

    AccessControl.VerifyAccess([PERMISSIONS.COMMUNITY.REPORTED_POSTS_LIST]) &&
      menuItems
        .find((menu) => menu.groupText === "Community")
        .items.push({ text: "Reported Posts", route: "/community/reports" })

    AccessControl.VerifyAccess([PERMISSIONS.COMMUNITY.REPORTED_COMMENTS_LIST]) &&
      menuItems
        .find((menu) => menu.groupText === "Community")
        .items.push({ text: "Reported Comments", route: "/community/reports/comments" })
  }

  // AI Management section (AI Agents, Voices, AI Review, MCP)
  ;(AccessControl.VerifyAccessByCategory(PERMISSIONS.AI_AGENT) ||
    AccessControl.VerifyAccess([PERMISSIONS.AI_AGENT.AI_AGENT_LIST]) ||
    AccessControl.VerifyAccessByCategory(PERMISSIONS.AI_PLATFORM)) &&
    menuItems.push({
      groupText: "AI Management",
      items: [],
    })

  AccessControl.VerifyAccess([PERMISSIONS.AI_AGENT.AI_AGENT_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "AI Management")
      .items.push({ text: "AI Agents", route: "/aiAgent" })

  // Add Voices management route if available/permissioned (backend permission name may differ)
  AccessControl.VerifyAccess([PERMISSIONS.AI_AGENT.AI_AGENT_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "AI Management")
      .items.push({ text: "Voices", route: "/ai-voices" })

  AccessControl.VerifyAccess([PERMISSIONS.AI_AGENT.AI_AGENT_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "AI Management")
      .items.push({ text: "AI Study Lab", route: "/ai-study-lab" })

  AccessControl.VerifyAccess([PERMISSIONS.AI_AGENT.AI_AGENT_KNOWLEDGE_BASE_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "AI Management")
      .items.push({ text: "Knowledge Base", route: "/knowledge-base" })

  canAccessAiPlatformAdmin &&
    menuItems
      .find((menu) => menu.groupText === "AI Management")
      ?.items.push({ text: "MCP Platform", route: "/mcp-platform/review" })

  // Menu for Products & Plans (subscription plans + one-off product gates)
  ;(AccessControl.VerifyAccessByCategory(PERMISSIONS.SUBSCRIPTION) ||
    AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_LIST])) &&
    menuItems.push({
      groupText: "Products & Plans",
      items: [],
    })

  AccessControl.VerifyAccess([
    PERMISSIONS.SUBSCRIPTION.SUBSCRIPTION_GET_ALL_PRODUCT_LIST,
  ]) &&
    menuItems
      .find((menu) => menu.groupText === "Products & Plans")
      .items.push({ text: "Subscription Product", route: "/product" })

  AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "Products & Plans")
      .items.push({ text: "Product Gates", route: "/productGates" })

  const marketplaceCategoryListPermission =
    PERMISSIONS.MARKETPLACE?.MARKETPLACE_CATEGORY_LIST ??
    MARKETPLACE_PERMISSIONS.MARKETPLACE_CATEGORY_LIST

  if (
    MARKETPLACE_MENU_ENABLED &&
    AccessControl.VerifyAccess([marketplaceCategoryListPermission])
  ) {
    menuItems.push({
      groupText: "Marketplace",
      items: [{ text: "Marketplace", route: "/marketplace" }],
    })
  }

  // Menu for User Management
  ;(AccessControl.VerifyAccessByCategory(PERMISSIONS.MEMBER) ||
    AccessControl.VerifyAccess([PERMISSIONS.MEMBER.MEMBER_DSAR_LIST]) ||
    canAccessPeriodicReports ||
    canAccessOnboarding) &&
    menuItems.push({
      groupText: "User Management",
      items: [],
    })

  AccessControl.VerifyAccess([PERMISSIONS.MEMBER.MEMBER_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "User Management")
      .items.push({ text: "Members", route: "/member" })

  AccessControl.VerifyAccess([PERMISSIONS.MEMBER.MEMBER_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "User Management")
      .items.push({ text: "Beta Testers", route: "/betaTesters" })

  AccessControl.VerifyAccess([PERMISSIONS.MEMBER.MEMBER_DSAR_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "User Management")
      .items.push({ text: "DSAR / Erase", route: "/member-dsar" })

  AccessControl.VerifyAccess([PERMISSIONS.MEMBER.MEMBER_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "User Management")
      .items.push({ text: "Badges", route: "/badges" })

  canAccessPeriodicReports &&
    menuItems
      .find((menu) => menu.groupText === "User Management")
      .items.push({ text: "Periodic Reports", route: "/periodicReports" })

  canAccessOnboarding &&
    menuItems
      .find((menu) => menu.groupText === "User Management")
      .items.push({ text: "Onboarding", route: "/onboarding" })

  canAccessOnboarding &&
    menuItems
      .find((menu) => menu.groupText === "User Management")
      .items.push({ text: "Welcome Series", route: "/onboardingSeries" })

  // Menu for System Management
  ;(AccessControl.VerifyAccessByCategory(PERMISSIONS.SYSTEM_SETTING) ||
    AccessControl.VerifyAccessByCategory(PERMISSIONS.SYSTEM_CONFIG) ||
    AccessControl.VerifyAccessByCategory(PERMISSIONS.ORDER) ||
    AccessControl.VerifyAccessByCategory(PERMISSIONS.RESOURCE)) &&
    menuItems.push({
      groupText: "System Management",
      items: [],
    })

  AccessControl.VerifyAccess([
    PERMISSIONS.SYSTEM_SETTING.SYSTEM_SETTING_LIST_ALL,
  ]) &&
    menuItems
      .find((menu) => menu.groupText === "System Management")
      .items.push({ text: "System Settings", route: "/systemSetting" })

  AccessControl.VerifyAccess([PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "System Management")
      .items.push({ text: "System Configs", route: "/systemConfig" })

  AccessControl.VerifyAccess([PERMISSIONS.NPS_QUESTION.NPS_QUESTION_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "System Management")
      .items.push({ text: "NPS Questions", route: "/npsQuestions" })

  canAccessBetaFeatures &&
    menuItems
      .find((menu) => menu.groupText === "System Management")
      .items.push({ text: "Beta Features", route: "/betaFeatures" })

  canAccessBetaFeatures &&
    menuItems
      .find((menu) => menu.groupText === "System Management")
      .items.push({ text: "Neuro-Fitness Games", route: "/neuroFitnessGames" })

  AccessControl.VerifyAccess([PERMISSIONS.RESOURCE.RESOURCE_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "System Management")
      .items.push({ text: "Media", route: "/media" })

  // Todo : need to change the permission type here
  AccessControl.VerifyAccess([PERMISSIONS.RESOURCE.RESOURCE_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "System Management")
      .items.push({ text: "Redis", route: "/redis" })

  // Menu for Admin Management
  ;(AccessControl.VerifyAccessByCategory(PERMISSIONS.ADMIN_USER) ||
    AccessControl.VerifyAccessByCategory(PERMISSIONS.ROLE) ||
    AccessControl.VerifyAccessByCategory(PERMISSIONS.PERMISSION)) &&
    menuItems.push({
      groupText: "Admin Management",
      items: [],
    })

  AccessControl.VerifyAccess([PERMISSIONS.ADMIN_USER.ADMIN_USER_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "Admin Management")
      .items.push({ text: "Admin User", route: "/admin" })

  AccessControl.VerifyAccess([PERMISSIONS.ROLE.ROLE_SEARCH]) &&
    menuItems
      .find((menu) => menu.groupText === "Admin Management")
      .items.push({ text: "Role", route: "/role" })

  AccessControl.VerifyAccess([PERMISSIONS.PERMISSION.PERMISSION_LIST]) &&
    menuItems
      .find((menu) => menu.groupText === "Admin Management")
      .items.push({ text: "Permission", route: "/permission" })

  const menuGroups = menuItems.filter((group) => group.items.length > 0)
  const insightsGroup =
    canAccessInsights && {
      groupText: "INSIGHTS",
      items: [
        { text: "Overview", route: "/insights/overview" },
        { text: "Users", route: "/insights/users" },
        { text: "AI", route: "/insights/ai" },
        { text: "Lessons", route: "/insights/lessons" },
        { text: "Events", route: "/insights/events" },
        { text: "Emails", route: "/insights/emails" },
        { text: "NPS", route: "/insights/nps" },
      ],
    }
  const orderedMenuGroups = [insightsGroup, ...menuGroups].filter(Boolean)
  const currentPath = location.pathname.toLowerCase()
  const isActiveRoute = (route) => {
    if (route === "/") {
      return currentPath === "/" || currentPath === ""
    }
    if (route === "/periodicReports") {
      return currentPath === "/periodicreports" || currentPath === "/order"
    }
    if (route === "/onboarding") {
      return currentPath === "/onboarding"
    }
    if (route === "/onboardingSeries") {
      return currentPath === "/onboardingseries"
    }
    if (route === "/insights/overview") {
      return currentPath === "/insights/overview" || currentPath === "/" || currentPath === ""
    }
    if (route === "/insights/lessons") {
      return currentPath === "/insights/lessons"
    }
    if (route === "/insights/users") {
      return currentPath === "/insights/users"
    }
    if (route === "/insights/ai") {
      return currentPath === "/insights/ai"
    }
    if (route === "/mcp-platform/review") {
      return currentPath.startsWith("/mcp-platform")
    }
    return currentPath === route.toLowerCase()
  }

  return (
    <LeftPanelWrapper>
      <PanelMenu>
        {orderedMenuGroups.map((group) => (
          <div className="menu-group" key={`group-${group.groupText}`}>
            <PanelMenuItem className="group-title">{group.groupText}</PanelMenuItem>
            {group.items.map((item) => (
              <PanelMenuItem
                key={`item-${group.groupText}-${item.route}`}
                className={`clickable ${isActiveRoute(item.route) ? "active" : ""}`}
              >
                <button onClick={() => navigate(item.route)}>
                  {item.text}
                </button>
              </PanelMenuItem>
            ))}
          </div>
        ))}
      </PanelMenu>
    </LeftPanelWrapper>
  )
}

export default LeftPanel
